import { appendCdnToImagePath } from '../../common/utils/appendCdnToImagePath';
import handleClickAwayLanguageSelection from '../utils/handleClickAwayLanguageSelection';
import LanguageSelectionForm from '../../common/components/LanguageSelectionForm';
import React, { useEffect, useState } from 'react';
import { faPlayCircle } from '@fortawesome/free-regular-svg-icons';
import { AssessmentDetails, LanguageDetail, TaskTileProps } from '../interfaces';
import { createLaunchConfig } from '../../common/utils/launchConfig';
import { faCalendarAlt, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getFormattedScheduledTaskDate } from '../../common/utils/dateFormatter';
import { Language } from '../../common/interfaces/language';
import { TaskItemStatus } from '../constants';
import { TaskTileStyles } from '../styles';
import { useTranslation } from 'react-i18next';
import { WorkflowItemAssessmentCodes, WorkflowItemCode } from '../../constants';
import {
  Box,
  CardActionArea,
  CardContent,
  CardHeader,
  CardMedia,
  LinearProgress,
  Divider,
  Typography,
  Hidden,
  ClickAwayListener,
} from '@material-ui/core';
import { useScheduledAssessmentTimer } from '../hooks';
import { ReactComponent as LockTileIcon } from '../images/lock_tile.svg';
import { ReactComponent as CompletedTaskIcon } from '../images/completed_task.svg';
import { ReactComponent as InProgressTaskIcon } from '../images/in_progress.svg';
import { ReactComponent as TimerIcon } from '../images/timer.svg';
import { isArabicLocale } from '../../common/utils/checkLocalePlayer';
import { initializeTask } from '../utils/initializeTask';
import { useHistory, useLocation } from 'react-router-dom';
import { useBasicDetails } from '../../store';

interface LockTileHOCProps {
  children?: React.ReactNode;
  className?: string;
}

/** Task Tile Component */
const TaskTile = (props: TaskTileProps): JSX.Element => {
  const {
    hasAnyTaskWithDescription,
    isTaskLocked,
    rowContainsScheduledAssessment,
    task,
    isSequentiallyLocked,
    showTileBanner,
    fetchAssessmentLaunguages,
  } = props;
  const { bannerImage, title, subtitle, description, duration, items, status } = task;
  const history = useHistory();
  const location = useLocation();
  const { userSessionActive } = useBasicDetails();
  const { t } = useTranslation();
  const classes = TaskTileStyles(props);

  const [isLanguageSelectionFormOpen, setLanguageSelectionFormOpen] = useState(false);
  const [assessmentDetail, setAssessmentDetail] = useState<AssessmentDetails>({
    availableLanguages: [],
    activeComponentId: 0,
    projectId: 0,
    packageId: 0,
    displayOptions: [],
    selectedLanguageCode: 'EN_US',
    sittingId: 0,
    defaultPackageLanguage: {
      testId: 0,
      language: {
        default: false,
        description: '',
        id: 0,
        integrationIsoCode: '',
        isoCode: '',
        name: '',
      },
    },
  });
  const [isLoadingAssessmentLanguages, setLoadingAssessmentLanguages] = useState(false);
  const scheduleableTask = task.items.filter(
    (item) => item.code === WorkflowItemCode.EXPASSES && item.isSchedulable && item.scheduled,
  );

  const schedulableAssessment = useScheduledAssessmentTimer(scheduleableTask[0]);

  /**
   * isLocked - Cases for schedulable
   * Case 1 - For sequential, lock task with first item gencontent untill it is sequentially active
   *              isSequentiallyLocked && task.items[0].code !== WorkflowItemCode.EXPASSES
   * Case 2 - For non sequential, unlock task with first item gencontent
   * Case 3 - For non sequential, lock task with first item assessment
   * Case 4 - For sequential, lock task with first item assessment
   *              (task.items[0].code === WorkflowItemCode.EXPASSES && schedulableAssessment.disabled)
   */
  const isLocked = task.isSchedulable
    ? (isSequentiallyLocked && task.items[0].code !== WorkflowItemCode.EXPASSES && schedulableAssessment.disabled) ||
      (task.items[0].code === WorkflowItemCode.EXPASSES && schedulableAssessment.disabled) ||
      task.status === TaskItemStatus.COMPLETED
    : isTaskLocked;

  const isVideoTask = task.items.some((item) => item.code === WorkflowItemCode.EXPVIDEOTASK);
  const handleTaskTileClick = async (): Promise<void> => {
    const firstNotStartedOrInProgressItem = items.find(
      (item) =>
        item.status === TaskItemStatus.IN_PROGRESS || !item.status || item.status === TaskItemStatus.NOT_STARTED,
    );
    const currentItem = firstNotStartedOrInProgressItem ? firstNotStartedOrInProgressItem : items[0];
    setLoadingAssessmentLanguages(true);
    if (WorkflowItemAssessmentCodes.includes(currentItem.code) && !currentItem.state) {
      if (!currentItem.isSchedulable) {
        setLanguageSelectionFormOpen(true);
      }

      const { data } = await fetchAssessmentLaunguages(currentItem);
      if (data && data.availableLanguages) {
        data.availableLanguages = data.availableLanguages.map((lang: LanguageDetail) => ({
          testId: lang.testId,
          activeComponentId: lang.activeComponentId,
          ...lang.language,
        }));
        setAssessmentDetail(data);

        if (data.availableLanguages.length === 1) {
          setLanguageSelectionFormOpen(false);
        }
      }
    } else {
      await initializeTask(task, userSessionActive, history, location);
    }

    setLoadingAssessmentLanguages(false);
  };

  const onLanguageSubmit = async (selectedLanguage: Language): Promise<void> => {
    const firstNotStartedOrInProgressItem = task.items.find(
      (item) =>
        !item.status || item.status === TaskItemStatus.NOT_STARTED || item.status === TaskItemStatus.IN_PROGRESS,
    );
    const currentItem = firstNotStartedOrInProgressItem ? firstNotStartedOrInProgressItem : task.items[0];
    // creating launch config
    const launchAssessmentConfig = createLaunchConfig(currentItem.id, task.id, selectedLanguage, assessmentDetail);
    await initializeTask(task, userSessionActive, history, location, launchAssessmentConfig);
  };

  // For forced langauge, directly launch the assessment on tile click
  useEffect(() => {
    if (!isLoadingAssessmentLanguages && assessmentDetail.availableLanguages.length === 1) {
      onLanguageSubmit(assessmentDetail.availableLanguages[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingAssessmentLanguages, assessmentDetail]);

  const scheduledTime = (): JSX.Element => {
    const date = scheduleableTask[0].startTime;
    const startTime = getFormattedScheduledTaskDate(parseInt(date ? date.replace('/Date(', '') : '', 10));
    return (
      <Box>
        <Box className={classes.scheduleStartTimeHat}>
          <FontAwesomeIcon
            icon={faCalendarAlt}
            size="lg"
            color="inherit"
            title={t('taskTileCompletedStatus')}
            aria-label={t('taskTileCompletedStatus')}
          />
          <Box component="span" pl={1}>
            {startTime}
          </Box>
        </Box>
      </Box>
    );
  };

  const LockTileHOC = ({ className, children }: LockTileHOCProps): JSX.Element => {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        className={`${className || ''} ${classes.IELockFix}`}
        title={!isLocked ? t('taskTileOpenStatus') : ''}
        aria-label={!isLocked ? t('taskTileOpenStatus') : ''}>
        {isLocked && (
          <Box className={classes.lock} title={t('taskTileLockedStatus')} aria-label={t('taskTileLockedStatus')}>
            <LockTileIcon width="77px" height="77px" />
          </Box>
        )}
        {!isLocked && isVideoTask && (
          <Box className={classes.lock}>
            <FontAwesomeIcon className={`${classes.taskIcon} ${classes.playIcon}`} icon={faPlayCircle} />
          </Box>
        )}
        {children}
      </Box>
    );
  };

  const TaskIcon = (): JSX.Element => {
    if (!status) {
      if (duration)
        return (
          <Box className={classes.timerIcon}>
            <TimerIcon
              className={classes.taskIcon}
              title={t('taskTileNotStartedStatus')}
              aria-label={t('taskTileNotStartedStatus')}
            />
            <span className={classes.timerText}>{`${duration} ${t('minuteAbbreviation')}`}</span>
          </Box>
        );
      else {
        return (
          <FontAwesomeIcon
            icon={isArabicLocale() ? faChevronLeft : faChevronRight}
            className={classes.taskIcon}
            title={t('taskTileOpenStatus')}
            aria-label={t('taskTileOpenStatus')}
          />
        );
      }
    } else if (status === TaskItemStatus.IN_PROGRESS || status === TaskItemStatus.EVALUATION_PENDING) {
      return (
        <InProgressTaskIcon
          className={classes.taskIcon}
          title={t('taskTileInProgressStatus')}
          aria-label={t('taskTileInProgressStatus')}
        />
      );
    } else if (status === TaskItemStatus.COMPLETED) {
      return (
        <CompletedTaskIcon
          className={classes.taskIcon}
          title={t('taskTileCompletedStatus')}
          aria-label={t('taskTileCompletedStatus')}
        />
      );
    }

    return <></>;
  };

  const TaskProgress = (): JSX.Element => {
    if (isLocked || (status !== TaskItemStatus.IN_PROGRESS && status !== TaskItemStatus.EVALUATION_PENDING))
      return <></>;

    const completedItemsCount = task.items.reduce(
      (count, item) =>
        count + +(item.status === TaskItemStatus.COMPLETED || item.status === TaskItemStatus.HURDLE_PASSED),
      0,
    );

    const width = Math.round((completedItemsCount * 100) / task.items.length);

    return <Box width={`${width}%`} className={classes.progressBar} />;
  };

  return (
    <Box className={`${classes.tileActionArea} ${classes.tile}`}>
      {task.isSchedulable && schedulableAssessment.disabled
        ? scheduledTime()
        : rowContainsScheduledAssessment && <Box height={44}></Box>}
      <CardActionArea
        component="div"
        className={classes.tileCard}
        onClick={handleTaskTileClick}
        disableRipple={isLanguageSelectionFormOpen}
        disabled={isLocked || isLoadingAssessmentLanguages}>
        {task.isSchedulable && !schedulableAssessment.disabled && task.status !== TaskItemStatus.COMPLETED ? (
          <LockTileHOC>
            <Box className={`${classes.tileHeader} ${classes.textCenter}`}>
              <Box className={classes.scheduledAssessmentBox}>
                <Typography color="inherit" className={classes.scheduledTask}>
                  {schedulableAssessment.message}
                </Typography>
              </Box>
            </Box>
          </LockTileHOC>
        ) : bannerImage && showTileBanner ? (
          <LockTileHOC>
            <CardMedia
              component="img"
              alt={bannerImage.name}
              className={classes.tileHeader}
              image={appendCdnToImagePath(bannerImage.path)}
            />
          </LockTileHOC>
        ) : (
          <LockTileHOC className={classes.tileHeader} />
        )}
        <TaskProgress />
        <CardHeader
          className={classes.actionIcon}
          action={
            <>
              <TaskIcon />
              {task.optional && (
                <Typography className={classes.optionalTask} variant="body2" color="inherit">
                  {t('optional')}
                </Typography>
              )}
            </>
          }
          title={
            <Typography variant="h3" color="inherit">
              {title}
            </Typography>
          }
          subheader={
            <Typography variant="body2" color="inherit">
              {subtitle}
            </Typography>
          }
        />
        <Hidden xsDown>
          <CardContent className={classes.tileDescription}>
            {hasAnyTaskWithDescription && <Divider className={classes.tileDivider} />}
            {description && (
              <Box py={2}>
                <Typography variant="body2" component="p" color="inherit">
                  {description}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Hidden>
        {isLanguageSelectionFormOpen && (
          <Box className={classes.languageSelectionOverlay} onClick={(ev): void => ev.stopPropagation()}>
            <ClickAwayListener
              onClickAway={(e: React.MouseEvent<Document, MouseEvent>): void => {
                handleClickAwayLanguageSelection(e, setLanguageSelectionFormOpen);
              }}>
              <Box className={classes.languageSelectionModal}>
                {isLoadingAssessmentLanguages ? (
                  <LinearProgress color="primary" />
                ) : (
                  <LanguageSelectionForm
                    languages={assessmentDetail.availableLanguages}
                    onLanguageSubmit={onLanguageSubmit}
                  />
                )}
              </Box>
            </ClickAwayListener>
          </Box>
        )}
      </CardActionArea>
    </Box>
  );
};

export default TaskTile;
